import { FC, useCallback, useEffect } from 'react';
import { Layer, Source, useMap } from 'react-map-gl';
import type mapboxGl from 'mapbox-gl';

/**
 * Type Curve Area geometry for the Type Curves and Workspaces listing views. The
 * map draws every area matching the current filters; the client sends the filter
 * payload on the tile url and the tile resolves and renders the full matching set
 * (`Map::Tiles::TypeCurveAreas`, features tagged with `id`).
 *
 * These views draw the Type Curve AREA rather than development-area units because
 * essentially every type curve has an area, while almost none have units.
 * Clicking a listing row highlights that row's area; clicking an area on the map
 * reports its id so the page selects the owning row.
 */

const SOURCE_ID = 'tc-listing-areas';
const SOURCE_LAYER = 'type-curves';
const FILL_LAYER_ID = `${SOURCE_ID}-fill`;

/** Selected area: filled so it reads at a glance, not just an outline. */
const SELECTED_COLOR = '#f1c21b';
const SELECTED_BORDER = '#b28600';

/** Matches no feature, ever — two unequal literals. */
const MATCH_NOTHING: mapboxGl.Expression = [
  '==',
  ['literal', 0],
  ['literal', 1],
];

/** Tiles emit a `shape` polygon and a `centroid` point per area. */
const SHAPE_ONLY: mapboxGl.Expression = ['==', ['get', 'type'], 'shape'];
const CENTROID_ONLY: mapboxGl.Expression = ['==', ['get', 'type'], 'centroid'];

interface TcListingAreasLayerProps {
  /** Vector tile template(s) from `useTcTileUrls` — carry the listing filters. */
  tileUrls: string[] | null;
  /** Selected Type Curve Area id, highlighted via the feature's `id`. */
  selectedAreaId?: number | null;
  /** Reports a clicked area's `id` so the page selects its row. */
  onSelectAreaId?: (areaId: number) => void;
}

export const TcListingAreasLayer: FC<TcListingAreasLayerProps> = ({
  tileUrls,
  selectedAreaId = null,
  onSelectAreaId,
}) => {
  const { current: mapRef } = useMap();

  const onClick = useCallback(
    (event: mapboxGl.MapLayerMouseEvent) => {
      const map = mapRef?.getMap();
      if (!map || !onSelectAreaId) return;
      const feature = map.queryRenderedFeatures(event.point, {
        layers: [FILL_LAYER_ID],
      })[0];
      const id = feature?.properties?.id;
      if (id === undefined || id === null) return;
      onSelectAreaId(Number(id));
    },
    [mapRef, onSelectAreaId]
  );

  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map) return undefined;
    map.on('click', FILL_LAYER_ID, onClick);
    return () => {
      map.off('click', FILL_LAYER_ID, onClick);
    };
  }, [mapRef, onClick]);

  // Mapbox must never see a partial source.
  if (!tileUrls) return null;

  const highlightFilter: mapboxGl.Expression =
    selectedAreaId === null
      ? MATCH_NOTHING
      : ['all', SHAPE_ONLY, ['==', ['get', 'id'], selectedAreaId]];

  // The base fill skips the selected shape: two semi-transparent fills stacked
  // composite (red under yellow reads orange), so the highlight only looks like
  // its own colour when nothing is painted beneath it.
  const baseFillFilter: mapboxGl.Expression =
    selectedAreaId === null
      ? SHAPE_ONLY
      : ['all', SHAPE_ONLY, ['!=', ['get', 'id'], selectedAreaId]];

  return (
    // `tiles` is the only mutating prop — see the note in
    // tc-listing-scenario-areas-layer.tsx for why.
    <Source id={SOURCE_ID} type="vector" tiles={tileUrls}>
      <Layer
        id={FILL_LAYER_ID}
        type="fill"
        source-layer={SOURCE_LAYER}
        filter={baseFillFilter}
        paint={{ 'fill-color': '#da1e28', 'fill-opacity': 0.15 }}
      />
      <Layer
        id={`${SOURCE_ID}-line`}
        type="line"
        source-layer={SOURCE_LAYER}
        filter={SHAPE_ONLY}
        paint={{
          'line-color': '#da1e28',
          'line-width': 2,
          'line-opacity': 0.8,
        }}
      />
      <Layer
        id={`${SOURCE_ID}-selected-fill`}
        type="fill"
        source-layer={SOURCE_LAYER}
        filter={highlightFilter}
        paint={{ 'fill-color': SELECTED_COLOR, 'fill-opacity': 0.55 }}
      />
      <Layer
        id={`${SOURCE_ID}-selected-line`}
        type="line"
        source-layer={SOURCE_LAYER}
        filter={highlightFilter}
        paint={{ 'line-color': SELECTED_BORDER, 'line-width': 2 }}
      />
      <Layer
        id={`${SOURCE_ID}-label`}
        type="symbol"
        source-layer={SOURCE_LAYER}
        filter={CENTROID_ONLY}
        minzoom={7}
        layout={{ 'text-field': ['get', 'name'], 'text-size': 11 }}
        paint={{
          'text-color': '#da1e28',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1,
        }}
      />
    </Source>
  );
};
